import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

const MUSIC_GENRES = [
  'Rock', 'Pop', 'Electronic / EDM', 'Hip-Hop / Rap', 'Jazz', 'Blues',
  'Clasic', 'Folk', 'Metal', 'Reggae', 'R&B / Soul', 'Latin',
  'Country', 'Alternative', 'Indie', 'Punk', 'Funk', 'World Music',
  'K-Pop', 'Techno', 'House', 'Trap', 'Manele', 'Etno'
];

const EMPTY_CONCERT = {
  title: '', price: '', old_price: '', short_description: '',
  operator: '', redirect_link: '', category: 'Concerte',
  image_url: '', is_early_booking: false, is_last_minute: false,
  is_active: true, country: '', city: '',
  artist_name: '', event_date: '', event_time: '',
  venue: '', music_genre: '', ticket_types: [],
  duration: 'Concert', transport_type: 'individual',
  keywords: [],
  useful_info: '',
  // Event-type toggle: "concert" or "eveniment"
  event_type: 'concert',
  event_group: '',
  event_subcategory: ''
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConcertForm({ initialData, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initialData ? { ...EMPTY_CONCERT, ...initialData } : EMPTY_CONCERT);
  const [errors, setErrors] = useState({});
  const [newTicketType, setNewTicketType] = useState('');
  const [eventCategories, setEventCategories] = useState({ Cultural: [], Sport: [], Altele: [] });

  useEffect(() => {
    if (initialData) setForm({ ...EMPTY_CONCERT, ...initialData });
    else setForm(EMPTY_CONCERT);
  }, [initialData]);

  useEffect(() => {
    fetch(`${API_URL}/api/event-categories`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setEventCategories(d); })
      .catch(() => {});
  }, []);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const addTicket = () => {
    if (newTicketType.trim()) {
      setForm(prev => ({ ...prev, ticket_types: [...(prev.ticket_types || []), newTicketType.trim()] }));
      setNewTicketType('');
    }
  };

  const removeTicket = (i) => {
    setForm(prev => ({ ...prev, ticket_types: prev.ticket_types.filter((_, idx) => idx !== i) }));
  };

  const validate = () => {
    const e = {};
    const isEvent = form.event_type === 'eveniment';
    if (!isEvent && !form.artist_name?.trim()) e.artist_name = 'Artistul este obligatoriu';
    if (!form.title?.trim()) e.title = 'Titlul este obligatoriu';
    if (!form.price?.trim()) e.price = 'Prețul este obligatoriu';
    if (!form.event_date) e.event_date = 'Data este obligatorie';
    if (!form.venue?.trim()) e.venue = 'Locația este obligatorie';
    if (!form.redirect_link?.trim()) e.redirect_link = 'Link-ul este obligatoriu';
    if (!form.image_url?.trim()) e.image_url = 'Imaginea este obligatorie';
    if (!form.short_description?.trim()) e.short_description = 'Descrierea este obligatorie';
    if (isEvent) {
      if (!form.event_group) e.event_group = 'Grupul este obligatoriu';
      if (!form.event_subcategory) e.event_subcategory = 'Subcategoria este obligatorie';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const data = { ...form, category: 'Concerte' };
      // Auto-generate title for concert if empty
      if (!data.title && data.artist_name) {
        data.title = `${data.artist_name} - Concert ${data.city || ''}`.trim();
      }
      // Clear concert-specific fields for events, and event fields for concerts
      if (data.event_type === 'eveniment') {
        data.artist_name = data.artist_name || '';
        data.music_genre = '';
      } else {
        data.event_group = '';
        data.event_subcategory = '';
      }
      onSave(data);
    }
  };

  const inputCls = (field) =>
    `w-full px-3 py-2 border rounded-lg text-slate-800 text-sm ${errors[field] ? 'border-red-400' : 'border-slate-300'}`;

  return (
    <form onSubmit={handleSubmit} className="space-y-5 bg-white rounded-xl p-6 border border-purple-200" data-testid="concert-form">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 text-lg font-bold">
            {form.event_type === 'eveniment' ? 'E' : 'C'}
          </div>
          <h3 className="text-lg font-bold text-slate-800">
            {initialData?.id ? (form.event_type === 'eveniment' ? 'Editare Eveniment' : 'Editare Concert') : (form.event_type === 'eveniment' ? 'Eveniment Nou' : 'Concert Nou')}
          </h3>
        </div>
      </div>

      {/* Type toggle: Concert vs Eveniment */}
      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
        <label className="block text-sm font-medium text-slate-700 mb-2">Tip</label>
        <div className="flex gap-2">
          <button type="button" onClick={() => set('event_type', 'concert')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${form.event_type === 'concert' ? 'bg-purple-600 text-white' : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'}`}
            data-testid="toggle-type-concert">
            Concert muzical
          </button>
          <button type="button" onClick={() => set('event_type', 'eveniment')}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${form.event_type === 'eveniment' ? 'bg-amber-500 text-white' : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'}`}
            data-testid="toggle-type-eveniment">
            Eveniment
          </button>
        </div>
      </div>

      {/* Event group + subcategory - only for evenimente */}
      {form.event_type === 'eveniment' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-amber-50 rounded-lg p-3 border border-amber-200">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Grup *</label>
            <select value={form.event_group || ''} onChange={e => { set('event_group', e.target.value); set('event_subcategory', ''); }}
              className={`w-full px-3 py-2 border rounded-lg text-slate-800 text-sm ${errors.event_group ? 'border-red-400' : 'border-slate-300'}`}
              data-testid="event-group-select">
              <option value="">Selectează grup...</option>
              <option value="Cultural">Cultural</option>
              <option value="Sport">Sport</option>
              <option value="Altele">Altele</option>
            </select>
            {errors.event_group && <p className="text-red-500 text-xs mt-1">{errors.event_group}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subcategorie *</label>
            <select value={form.event_subcategory || ''} onChange={e => set('event_subcategory', e.target.value)}
              disabled={!form.event_group}
              className={`w-full px-3 py-2 border rounded-lg text-slate-800 text-sm disabled:bg-slate-100 disabled:cursor-not-allowed ${errors.event_subcategory ? 'border-red-400' : 'border-slate-300'}`}
              data-testid="event-subcategory-select">
              <option value="">Selectează subcategorie...</option>
              {(eventCategories[form.event_group] || []).map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
            {errors.event_subcategory && <p className="text-red-500 text-xs mt-1">{errors.event_subcategory}</p>}
            {form.event_group && (eventCategories[form.event_group] || []).length === 0 && (
              <p className="text-amber-600 text-xs mt-1 italic">Nu există subcategorii. Adaugă din tabul "Categorii Evenimente".</p>
            )}
          </div>
        </div>
      )}

      {/* Artist + Title */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            {form.event_type === 'eveniment' ? 'Artist / Trupă (opțional)' : 'Artist / Eveniment *'}
          </label>
          <input type="text" value={form.artist_name || ''} onChange={e => { set('artist_name', e.target.value); if (!form.title || form.title === `${form.artist_name} - Concert`) set('title', `${e.target.value} - Concert`); }}
            className={inputCls('artist_name')} placeholder="ex: Coldplay" data-testid="concert-artist" />
          {errors.artist_name && <p className="text-red-500 text-xs mt-1">{errors.artist_name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Titlu complet *</label>
          <input type="text" value={form.title || ''} onChange={e => set('title', e.target.value)}
            className={inputCls('title')} placeholder="ex: Coldplay - Music of the Spheres Tour" data-testid="concert-title" />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
        </div>
      </div>

      {/* Date + Time + Genre (genre only for concerts) */}
      <div className={`grid grid-cols-1 ${form.event_type === 'eveniment' ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Data eveniment *</label>
          <input type="date" value={form.event_date || ''} onChange={e => set('event_date', e.target.value)}
            className={inputCls('event_date')} data-testid="concert-date" />
          {errors.event_date && <p className="text-red-500 text-xs mt-1">{errors.event_date}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Ora</label>
          <input type="time" value={form.event_time || ''} onChange={e => set('event_time', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" />
        </div>
        {form.event_type !== 'eveniment' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Gen muzical</label>
            <select value={form.music_genre || ''} onChange={e => set('music_genre', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" data-testid="concert-genre">
              <option value="">Selectează...</option>
              {MUSIC_GENRES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Venue + City + Country */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Locație / Arenă *</label>
          <input type="text" value={form.venue || ''} onChange={e => set('venue', e.target.value)}
            className={inputCls('venue')} placeholder="ex: Arena Națională" data-testid="concert-venue" />
          {errors.venue && <p className="text-red-500 text-xs mt-1">{errors.venue}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Oraș</label>
          <input type="text" value={form.city || ''} onChange={e => set('city', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="ex: București" data-testid="concert-city" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Țara</label>
          <input type="text" value={form.country || ''} onChange={e => set('country', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="ex: România" />
        </div>
      </div>

      {/* Price + Old Price + Operator */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Preț de la *</label>
          <input type="text" value={form.price || ''} onChange={e => set('price', e.target.value)}
            className={inputCls('price')} placeholder="290 EUR" data-testid="concert-price" />
          {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Preț vechi</label>
          <input type="text" value={form.old_price || ''} onChange={e => set('old_price', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="350 EUR" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Operator</label>
          <input type="text" value={form.operator || ''} onChange={e => set('operator', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm" placeholder="Bilete.ro" />
        </div>
      </div>

      {/* Image + Redirect */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">URL Imagine *</label>
          <input type="text" value={form.image_url || ''} onChange={e => set('image_url', e.target.value)}
            className={inputCls('image_url')} placeholder="https://..." data-testid="concert-image" />
          {errors.image_url && <p className="text-red-500 text-xs mt-1">{errors.image_url}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Link afiliat *</label>
          <input type="text" value={form.redirect_link || ''} onChange={e => set('redirect_link', e.target.value)}
            className={inputCls('redirect_link')} placeholder="https://bilete.ro/..." data-testid="concert-link" />
          {errors.redirect_link && <p className="text-red-500 text-xs mt-1">{errors.redirect_link}</p>}
        </div>
      </div>

      {/* Image Preview */}
      {form.image_url && (
        <div className="flex items-center gap-3">
          <img src={form.image_url} alt="Preview" className="w-24 h-16 rounded-lg object-cover border"
            onError={e => { e.target.style.display = 'none'; }} />
          <span className="text-xs text-slate-400">Preview imagine</span>
        </div>
      )}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Descriere *</label>
        <textarea value={form.short_description || ''} onChange={e => set('short_description', e.target.value)}
          className={`${inputCls('short_description')} h-20`}
          placeholder="Descriere eveniment..." data-testid="concert-description" />
        {errors.short_description && <p className="text-red-500 text-xs mt-1">{errors.short_description}</p>}
      </div>

      {/* Alte informații (useful_info) - text liber, oricât de lung */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Alte informații</label>
        <textarea
          value={form.useful_info || ''}
          onChange={e => set('useful_info', e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm min-h-[120px] resize-y"
          placeholder="Informații suplimentare: condiții speciale, recomandări, instrucțiuni de acces, dotări, restricții, etc. Textul va fi vizibil integral pe pagina pachetului."
          data-testid="concert-useful-info"
        />
        <p className="text-xs text-slate-500 mt-1">Acest text va fi afișat integral pe pagina detaliilor pachetului (fără trunchiere).</p>
      </div>

      {/* Ticket types */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Tipuri bilete disponibile</label>
        <div className="flex gap-2">
          <input type="text" value={newTicketType} onChange={e => setNewTicketType(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTicket(); } }}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-slate-800 text-sm"
            placeholder="ex: Golden Circle, Tribuna I, Gazon" data-testid="concert-ticket-input" />
          <button type="button" onClick={addTicket}
            className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700">+</button>
        </div>
        {form.ticket_types?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {form.ticket_types.map((t, i) => (
              <span key={i} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full flex items-center gap-1">
                {t} <button type="button" onClick={() => removeTicket(i)} className="hover:text-red-500">&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status toggles */}
      <div className="border-t border-slate-200 pt-4 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
            className="rounded border-slate-300" />
          <span className="text-sm text-slate-700">Activ</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_early_booking} onChange={e => set('is_early_booking', e.target.checked)}
            className="rounded border-slate-300" />
          <span className="text-sm text-slate-700">Early Booking</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_last_minute} onChange={e => set('is_last_minute', e.target.checked)}
            className="rounded border-slate-300" />
          <span className="text-sm text-slate-700">Last Minute</span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors" data-testid="concert-cancel">
          Anulează
        </button>
        <button type="submit" disabled={loading}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2" data-testid="concert-save">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {initialData?.id ? 'Salvează' : 'Adaugă concertul'}
        </button>
      </div>
    </form>
  );
}
